/**
 * The overview: a bird's-eye view of every baby for the moments between
 * feeds. What may have been forgotten, new milestones, a box score of right
 * now, then trends (bottles, changes, and sleep in tabs, changes and sleep
 * only if the family tracks them) by day over the last two weeks or by week
 * or month of each baby's life, bottles by hour of the day over the two
 * weeks, each baby's weights on the WHO growth curves, notes, each baby's
 * all-time firsts, the way to the pediatrician report, and a word to Babia's
 * makers. Read-only — logging stays one tap away on the log.
 *
 * Every baby sits side by side from the md breakpoint up. On a phone, where
 * the columns would stack, the log's baby tabs show one baby's sections at a
 * time, from Trends to Day by day.
 */
import { countedDays, currentNightStart, localCalendar, periodRows } from "@babia/checks";
import { Link } from "@tanstack/react-router";
import { useId, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { FeedbackButton } from "../components/feedback.tsx";
import { BabyTabs } from "../components/log/baby-tabs.tsx";
import { BottlesByHour } from "../components/overview/bottles-by-hour.tsx";
import { BoxScore } from "../components/overview/box-score.tsx";
import { DayTable } from "../components/overview/day-table.tsx";
import { Growth } from "../components/overview/growth.tsx";
import {
  MilestoneList,
  MilestoneNews,
  recentMilestones,
} from "../components/overview/milestones.tsx";
import { NeedsALook } from "../components/overview/needs-a-look.tsx";
import { NoteCloud } from "../components/overview/note-cloud.tsx";
import { PerBaby, Section } from "../components/overview/section.tsx";
import { Trends } from "../components/overview/trends.tsx";
import { LogHeader } from "../components/log/header.tsx";
import { UpdateNotice } from "../components/update-notice.tsx";
import { track } from "../lib/analytics.ts";
import {
  bottleHours,
  chartStart,
  dailyTotals,
  type DayTotals,
  firstFullDay,
  glance,
  loggingBegan,
  mergeHistory,
  needsALook,
  sleepRows,
} from "../lib/overview.ts";
import { weightPoints } from "../lib/growth-chart.ts";
import { noteWords } from "../lib/note-words.ts";
import { seriesSince, type Zoom, ZOOM_UNIT } from "../lib/periods.ts";
import {
  useBabies,
  useBottleUnit,
  useEntries,
  useEntryHistory,
  useEntrySeries,
  useMilestones,
  useTracking,
  useWeights,
  useWeightUnit,
} from "../lib/queries.ts";
import { REPORT_DAYS, reportContents } from "../lib/report.ts";
import { useSelectedBaby } from "../lib/selected-baby.tsx";
import { LIVE_WINDOW_MS } from "../lib/timeline.ts";
import { ago, clock } from "../lib/time.ts";
import { useMediaQuery, WIDE } from "../lib/use-media-query.ts";
import { useNow } from "../lib/use-now.ts";

/**
 * Runs a tab switch and keeps the section being read where it was on screen.
 * The other baby's sections can be taller or shorter (more milestones, a
 * birthday prompt for a chart), which would move everything below them, and
 * iOS Safari has no scroll anchoring to make up for it. The section being
 * read is the one across a line a third of the way down the screen under the
 * tabs, so the last strip of the section above never counts.
 */
function switchInPlace(panel: HTMLElement | null, tabsBottom: number, change: () => void) {
  const line = tabsBottom + (window.innerHeight - tabsBottom) / 3;
  const anchor = [...(panel?.querySelectorAll("section") ?? [])].find(
    (s) => s.getBoundingClientRect().bottom > line,
  );
  const before = anchor?.getBoundingClientRect().top;
  flushSync(change);
  if (anchor && before !== undefined)
    window.scrollBy(0, anchor.getBoundingClientRect().top - before);
}

/** The rail's freshness note; loud only when the live log can't refresh. */
function Freshness({
  updatedAt,
  failing,
  now,
}: {
  updatedAt: number;
  failing: boolean;
  now: Date;
}) {
  if (!updatedAt) return <>Loading…</>;
  const stale = now.getTime() - updatedAt > 60_000;
  if (failing && stale)
    return (
      <span className="text-accent-2 opacity-100">
        Offline — showing data from {ago(new Date(updatedAt), now)} ago
      </span>
    );
  return <>Updated {stale ? `${ago(new Date(updatedAt), now)} ago` : "just now"}</>;
}

export function Overview() {
  const babies = useBabies();
  const live = useEntries();
  const history = useEntryHistory();
  const milestones = useMilestones();
  const tracking = useTracking();
  const unit = useBottleUnit();
  const now = useNow(20_000);
  const list = useMemo(() => babies.data ?? [], [babies.data]);
  // The phone's tabs: the log's selected baby, so the overview opens on the
  // baby you were just logging, and a switch here carries back to the log.
  // Every scale is shared, so a switch compares like with like.
  const { babyId, setBabyId } = useSelectedBaby();
  const wide = useMediaQuery(WIDE);
  const panelId = useId();
  const tabs = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const tabbed = !wide && list.length > 1;
  const selected = list.find((b) => b.id === babyId) ?? list[0];
  const shown = useMemo(() => (tabbed && selected ? [selected] : list), [tabbed, selected, list]);

  const entries = useMemo(
    () => mergeHistory(live.data, history.data, now),
    [live.data, history.data, now],
  );
  const from = useMemo(() => chartStart(entries, now), [entries, now]);
  const historyReady = history.data !== undefined || history.isError;
  // Never loaded: the overview has only the live two days to go on. (A failed
  // background refresh keeps the two weeks already loaded.)
  const historyMissing = history.data === undefined && history.isError;

  const per = useMemo(() => {
    const days = new Map<string, DayTotals[]>();
    for (const b of list) days.set(b.id, dailyTotals(entries, b.id, now, from));
    return days;
  }, [entries, list, now, from]);
  // Bottles by hour: the same two weeks, full days only. The milestones know
  // when logging began; until they load, the two weeks give a close guess.
  // With only the live 48 hours, the count starts at the first day they hold
  // in full.
  const liveFrom = historyMissing ? live.dataUpdatedAt - LIVE_WINDOW_MS : null;
  const hours = useMemo(() => {
    const began = loggingBegan(
      entries,
      now,
      milestones.data?.map((m) => m.trackingSince),
    );
    const full = liveFrom === null ? null : firstFullDay(new Date(liveFrom));
    const start = full && from < full ? full : from;
    return new Map(list.map((b) => [b.id, bottleHours(entries, b.id, now, start, began)]));
  }, [entries, list, now, from, milestones.data, liveFrom]);
  // Trends by week or month of life: their own read, from the oldest month
  // any baby shows, made only once a parent picks Weeks or Months. The counts
  // move only at midnight (today) and at noon (the night just over).
  const [zoom, setZoom] = useState<Zoom>("days");
  const today = localCalendar.isoDate(now);
  const since = useMemo(() => seriesSince(list, today)?.toISOString() ?? null, [list, today]);
  const series = useEntrySeries(since, zoom !== "days");
  const nightCutoff = currentNightStart(now, localCalendar).getTime();
  const counted = useMemo(() => {
    const began = series.data?.began ?? null;
    const cutoff = new Date(nightCutoff);
    return new Map(
      (series.data?.babies ?? []).map((s) => [
        s.babyId,
        countedDays(s, began, today, cutoff, localCalendar),
      ]),
    );
  }, [series.data, today, nightCutoff]);
  const periods = useMemo(
    () => (zoom === "days" ? null : periodRows(counted, list, today, ZOOM_UNIT[zoom])),
    [counted, list, today, zoom],
  );
  // The growth chart: every stored weight, with the live list laid over it.
  const weights = useWeights();
  const weightUnit = useWeightUnit();
  const points = useMemo(
    () => weightPoints(live.data, weights.data, now),
    [live.data, weights.data, now],
  );
  const glances = useMemo(
    () => new Map(list.map((b) => [b.id, glance(entries, b.id, now, unit)])),
    [entries, list, now, unit],
  );
  // A sleeping baby's tab carries 😴, as on the log.
  const asleep = useMemo(
    () =>
      new Set(
        tracking.sleep ? list.filter((b) => glances.get(b.id)?.state?.asleep).map((b) => b.id) : [],
      ),
    [list, glances, tracking.sleep],
  );
  // Checked only once the household says what it tracks, so a turned-off
  // type never flashes up.
  const checkReady = historyReady && tracking.ready;
  const attention = useMemo(
    () => (checkReady ? needsALook(entries, list, now, tracking.untracked) : []),
    [entries, list, now, checkReady, tracking.untracked],
  );

  const nameOf = (id: string) => list.find((b) => b.id === id)?.name ?? "";
  // Every baby's names and nicknames: notes mention the twin too.
  const names = list.flatMap((b) => [b.name, ...b.aliases]);
  const contents = reportContents(tracking);

  return (
    <>
      <LogHeader
        now={now}
        view="overview"
        rail={<Freshness updatedAt={live.dataUpdatedAt} failing={live.isError} now={now} />}
      />
      <UpdateNotice />

      <NeedsALook items={attention} babies={list} checking={!checkReady} limited={historyMissing} />
      <MilestoneNews
        items={recentMilestones(milestones.data ?? [], now)}
        nameOf={nameOf}
        unit={unit}
      />
      <BoxScore babies={list} glances={glances} now={now} tracking={tracking} unit={unit} />

      {historyMissing ? (
        <p className="mt-6 text-[14px] text-accent-2">
          Couldn't load the last two weeks — {history.error?.message}. Showing the last two days.
        </p>
      ) : history.isRefetchError ? (
        <p className="mt-6 text-[13px] opacity-70">
          Couldn't refresh the older days — showing them as of{" "}
          {clock(new Date(history.dataUpdatedAt))}. The last two days are live.
        </p>
      ) : null}

      {/* The tabs stick to the top while the sections they switch scroll by. */}
      <div>
        {tabbed ? (
          <div
            ref={tabs}
            className="sticky top-[env(safe-area-inset-top)] z-10 mt-10 bg-paper py-2"
          >
            <BabyTabs
              babies={list}
              selectedId={selected?.id ?? null}
              asleep={asleep}
              panelId={panelId}
              className="mt-0"
              onSelect={(id) => {
                if (id === selected?.id) return;
                track("baby tab switched", { babies: list.length });
                switchInPlace(
                  panel.current,
                  tabs.current?.getBoundingClientRect().bottom ?? 0,
                  () => setBabyId(id),
                );
              }}
            />
          </div>
        ) : null}
        {/* isolate: a chart's tooltip scrolls under the stuck tabs, not over them. */}
        <div
          ref={panel}
          role={tabbed ? "tabpanel" : undefined}
          id={panelId}
          className={tabbed ? "isolate" : undefined}
          aria-labelledby={tabbed && selected ? `${panelId}-${selected.id}` : undefined}
        >
          <Trends
            babies={shown}
            all={list}
            per={per}
            sleepRows={(id) => sleepRows(entries, id, now, from)}
            tracking={tracking}
            unit={unit}
            loading={!historyReady}
            limited={historyMissing}
            zoom={zoom}
            onZoom={setZoom}
            long={{
              periods: series.data === undefined ? null : periods,
              loading: since !== null && series.data === undefined && !series.isError,
              error: series.data === undefined && series.isError ? series.error.message : null,
              staleSince: series.isRefetchError ? series.dataUpdatedAt : null,
            }}
          />

          <BottlesByHour
            babies={shown}
            hours={hours}
            unit={unit}
            loading={!historyReady}
            limited={historyMissing}
          />

          <Growth
            babies={shown}
            all={list}
            points={points}
            unit={weightUnit}
            loading={weights.data === undefined && !weights.isError}
            error={weights.data === undefined && weights.isError ? weights.error.message : null}
            staleSince={weights.isRefetchError ? weights.dataUpdatedAt : null}
            now={now}
          />

          <div aria-busy={!historyReady} className={historyReady ? undefined : "opacity-50"}>
            <Section title="Notes" note="Two weeks of notes · bigger means more notes mention it">
              <PerBaby babies={shown}>
                {(b) => (
                  <NoteCloud
                    words={noteWords(entries, b.id, names)}
                    notes={entries.filter((e) => e.babyId === b.id && e.type === "note")}
                    loading={!historyReady}
                    limited={historyMissing}
                  />
                )}
              </PerBaby>
            </Section>
          </div>

          <Section title="Milestones" note="All time">
            <PerBaby babies={shown}>
              {(b) => (
                <MilestoneList
                  data={milestones.data?.find((m) => m.babyId === b.id)}
                  nameOf={nameOf}
                  unit={unit}
                  sleep={tracking.sleep}
                />
              )}
            </PerBaby>
          </Section>

          <Section title="Day by day" note="The last two weeks, as a table">
            <PerBaby babies={shown}>
              {(b) => (
                <details className="group">
                  <summary className="ghost-btn -ml-2 inline-flex min-h-[44px] cursor-pointer items-center px-2 text-[14px]">
                    <span className="group-open:hidden">Show the table</span>
                    <span className="hidden group-open:inline">Hide the table</span>
                  </summary>
                  <DayTable days={per.get(b.id) ?? []} tracking={tracking} unit={unit} />
                </details>
              )}
            </PerBaby>
          </Section>
        </div>
      </div>

      <Section title="For the pediatrician" note={`The last ${REPORT_DAYS} full days`}>
        <p className="mt-3 max-w-[60ch] text-[14px] leading-snug">
          {contents.charAt(0).toUpperCase() + contents.slice(1)}
          {list.length > 1 ? " for each baby" : ""}, ready to print or save as a PDF for a checkup.
        </p>
        <Link
          to="/report"
          className="primary-btn heading mt-3 inline-flex min-h-[44px] items-center px-4 text-[16px]"
        >
          Open the report
        </Link>
      </Section>

      <footer className="mt-12 border-t border-divider pt-3">
        <FeedbackButton
          placement="overview"
          className="ghost-btn -ml-2 inline-flex min-h-[44px] items-center gap-2 px-2 text-[14px]"
        >
          <span aria-hidden="true">💬</span>
          Tell us what you think of Babia
        </FeedbackButton>
      </footer>
    </>
  );
}
